// Config
import config from '../config'

// Utils
import { getItem, setItem } from './localStorage'
import { isEventAcceptableInTime, getTodayDate } from './utils'
import { isTriggerSlotValueMatchingWebchatSlot } from './slot'
import Bus from '../Bus'

class PushNotification {
  constructor() {
    this.timeTrackingNotificationInterval = null
    this.pushNotifications = []
    this.assistantId = null
    this.websiteTimeTracking = null
    this.inactivityTimer = null
  }

  /**
   * Validate if the push notification of type SLOT_FILLED_ON_LOAD can be triggered
   * by checking all the trigger slots conditions against the webchat slots
   * @param {Object} pushNotification
   * @param {Object} webchatSlots The webchat slots object with {slug: value} pairs
   * @returns {Boolean} True if all trigger slot conditions are met, false otherwise
   */
  isSlotFilledNotificationValid(pushNotification, webchatSlots) {
    if (!pushNotification.triggerSlots || !pushNotification.triggerSlots.length) {
      return false
    }
    return pushNotification.triggerSlots.every(triggerSlot => {
      return isTriggerSlotValueMatchingWebchatSlot(triggerSlot, webchatSlots)
    })
  }

  /**
   * Initialize the pushNotification interval to check for the possible invocation of the push notification
   * @param {Array<Object>} pushNotifications
   * @param {String} assistantId
   * @param {String} timeZone
   * @param {Number} pushNotificationWaitTime
   * @param {Object} storeGetter
   */
  initPushNotification(pushNotifications, assistantId, timeZone, pushNotificationWaitTime, storeGetter) {
    this.pushNotifications = pushNotifications
    this.assistantId = assistantId
    this.pushNotificationWaitTime = pushNotificationWaitTime

    if (!this.pushNotifications.length) {
      return
    }

    let lowestInactivityPushNotification = null
    let shouldInitiateLeaveViewportNotification = false
    const formattedWebchatSlots = storeGetter.webchatSlots.reduce((acc, obj) => ({ ...acc, ...obj }), {})

    this.pushNotifications.forEach(pushNotification => {
      // Initialize the website time spent tracking for TIME_ON_WEBSITE notification type
      if (pushNotification.triggerType === config.TRIGGER_TYPES.TIME_ON_WEBSITE && this.websiteTimeTracking === null) {
        this.initWebsiteTimeTracking(timeZone)
        // Start the setInterval when TIME_ON_WEBSITE presents
        this.timeTrackingNotificationInterval = setInterval(
          this.handleTimeTrackingNotificationInterval.bind(this),
          config.NOTIFICATION_INTERVAL
        )
        return
      }

      // For SLOT_FILLED_ON_LOAD type check the slots against webchat slots and emit triggerPushNotification directly
      if (
        pushNotification.triggerType === config.TRIGGER_TYPES.SLOT_FILLED_ON_LOAD &&
        this.isSlotFilledNotificationValid(pushNotification, formattedWebchatSlots)
      ) {
        Bus.$emit('triggerPushNotification', pushNotification)
        return
      }

      /**
       * For INACTIVITY_DURATION type check the urls with parent frame url and pick the lowest inactivityDuration
       * when multiple notification of type INACTIVITY_DURATION presents
       */
      lowestInactivityPushNotification = this.getInactivityDurationNotification(
        pushNotification,
        lowestInactivityPushNotification,
        storeGetter.parentUrl
      )

      /**
       * For LEAVE_VIEWPORT type check urls with parent frame url
       */
      if (pushNotification.triggerType === config.TRIGGER_TYPES.LEAVE_VIEWPORT) {
        shouldInitiateLeaveViewportNotification = true
        return
      }
    })

    // Initialize inactivity tracking only if notification of type INACTIVITY_DURATION presents
    if (lowestInactivityPushNotification !== null) {
      /** If we pass the parentUrl when resetInactivityTimer is called parentUrl may not be same as the current parentUrl,
       * so if storeGetter is passed always current parentUrl is referenced */
      this.initInactivityTracking(lowestInactivityPushNotification, storeGetter)
    }

    // Initialize leave viewport tracking only if notification of type LEAVE_VIEWPORT presents
    if (shouldInitiateLeaveViewportNotification) {
      this.initLeaveViewportTracking()
    }
  }

  /**
   * Update the pushNotifications to remove the sent pushNotification from the list
   * @param {Array<Object>} pushNotifications
   */
  setPushNotifications(pushNotifications) {
    this.pushNotifications = pushNotifications
  }

  /**
   * Get and set the website Time tracking localStorage
   * @param {String} timeZone
   */
  initWebsiteTimeTracking(timeZone) {
    // Get assistant tracking from local storage if exists and update websiteTimeTracking
    /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
    try {
      const today = getTodayDate(timeZone)
      this.websiteTimeTracking = {
        timeSpent: config.NOTIFICATION_INIT_SECS,
        dateDay: today
      }
      this.initTimeSpent()
      // Reset dateDay to today
      this.websiteTimeTracking.dateDay = today
    } catch (err) {}

    // Update localStorage
    setItem(`websiteTimeTracking${this.assistantId}`, JSON.stringify(this.websiteTimeTracking))
  }

  /**
   * Initialize time spent on website value from localStorage
   */
  initTimeSpent() {
    const websiteTimeTracking = JSON.parse(getItem(`websiteTimeTracking${this.assistantId}`, '{}'))
    // If timeSpent was not tracked today, reset it to NOTIFICATION_INIT_SECS
    this.websiteTimeTracking.timeSpent =
      (websiteTimeTracking &&
        websiteTimeTracking.dateDay === this.websiteTimeTracking.dateDay &&
        websiteTimeTracking.timeSpent) ||
      this.websiteTimeTracking.timeSpent
  }

  /**
   * Update websiteTimeTracking object and the localStorage
   */
  updateWebsiteTimeTracking() {
    if (this.websiteTimeTracking === null) {
      return
    }

    this.websiteTimeTracking.timeSpent =
      parseInt(this.websiteTimeTracking.timeSpent) + config.NOTIFICATION_INTERVAL / 1000
    // Update localStorage
    setItem(`websiteTimeTracking${this.assistantId}`, JSON.stringify(this.websiteTimeTracking))
  }

  /**
   * Function to handle the push notification interval
   */
  handleTimeTrackingNotificationInterval() {
    // Update the website time spent
    this.updateWebsiteTimeTracking()
    // When conditions met check and trigger the push notification
    this.checkAndTriggerPushNotification()
  }

  /**
   * Initialize the inactivity tracking
   * @param {Object} pushNotification
   * @param {object} storeGetter
   */
  initInactivityTracking(pushNotification, storeGetter) {
    // Prepare the event callback method
    this.resetInactivityTimerBind = this.resetInactivityTimer.bind(this, pushNotification, storeGetter)
    // Attach the event listeners
    this.handleEventListeners()
    // Timer starts only on any user activity to start immediately resetInactivityTimer is called
    this.resetInactivityTimer(pushNotification, storeGetter)
  }

  /**
   * Callback method for inactivity events
   * @param {Object} pushNotification
   * @param {object} storeGetter
   */
  resetInactivityTimer(pushNotification, storeGetter) {
    // When the one of events triggered clear the timeout
    clearTimeout(this.inactivityTimer)
    // Calculate the duration in milliseconds
    const duration = pushNotification.inactivityDuration * 1000

    // Set the timeout with calculated duration so that when the user is inactive for ${duration} the notification will be triggered
    this.inactivityTimer = setTimeout(() => {
      // Check and trigger the push notification
      const isTriggered = this.checkAndTriggerPushNotification(pushNotification, storeGetter.parentUrl)
      // Remove the event listeners only when notification is triggered
      if (isTriggered) {
        this.handleEventListeners(true)
        this.resetInactivityTimerBind = null
      }
    }, duration)
  }

  /**
   * Compare the existing lowestNotification duration with notification duration
   * and return the one with the smallest duration only if notification.urls contain parentUrl
   * @param {Object} pushNotification
   * @param {Object} lowestDurationNotification - notification to compare
   * @param {String} parentUrl
   * @returns {Object}
   */
  getInactivityDurationNotification(pushNotification, lowestDurationNotification, parentUrl) {
    if (pushNotification.triggerType !== config.TRIGGER_TYPES.INACTIVITY_DURATION) {
      return lowestDurationNotification
    }

    if (
      lowestDurationNotification &&
      lowestDurationNotification.inactivityDuration < pushNotification.inactivityDuration
    ) {
      return lowestDurationNotification
    }

    return this.isParentUrlInListOfUrls(parentUrl, pushNotification.urls)
      ? pushNotification
      : lowestDurationNotification
  }

  /**
   * Check and reinitialize the inactivity type notification
   * @param {Object} storeGetter
   */
  checkAndReInitiateInactivityNotification(storeGetter) {
    // clear the listeners
    this.handleEventListeners(true)
    this.resetInactivityTimerBind = null

    if (!this.pushNotifications || !this.pushNotifications.length) {
      return
    }

    let lowestInactivityPushNotification = null
    this.pushNotifications.forEach(pushNotification => {
      lowestInactivityPushNotification = this.getInactivityDurationNotification(
        pushNotification,
        lowestInactivityPushNotification,
        storeGetter.parentUrl
      )
    })

    // Reinitialize inactivity tracking only if another notification of type INACTIVITY_DURATION presents
    if (lowestInactivityPushNotification !== null) {
      this.initInactivityTracking(lowestInactivityPushNotification, storeGetter)
    }
  }

  /**
   * Toggle the event listeners for inactivity detection
   * @param {Boolean} shouldRemoveEvent - default: false
   */
  handleEventListeners(shouldRemoveEvent = false) {
    const eventHandler = window[shouldRemoveEvent ? 'removeEventListener' : 'addEventListener']
    config.USER_ACTIVITY_EVENT_TYPES.forEach(event => {
      eventHandler(event, this.resetInactivityTimerBind)
    })
  }

  /**
   * Initialize the leave viewport by send the event to chat plugin
   */
  initLeaveViewportTracking() {
    parent.postMessage(config.FRAME_EVENT_TYPES.SENT.LISTEN_LEAVE_VIEWPORT, '*')
  }

  /**
   * Check and trigger the pushNotification
   * @param {Object} pushNotification - default: null
   * @param {String} pushNotification._id
   * @param {String} pushNotification.triggerType
   * @param {Array<String>} pushNotification.urls
   * @param {Number} pushNotification.timeBeforeTrigger
   * @param {Number} pushNotification.inactivityDuration
   * @param {String} parentUrl - default: null
   * @returns {Boolean}
   */
  checkAndTriggerPushNotification(pushNotification = null, parentUrl = null) {
    // When the triggerType is INACTIVITY_DURATION check the urls with the frame url and emit triggerPushNotification
    if (
      pushNotification &&
      pushNotification.triggerType === config.TRIGGER_TYPES.INACTIVITY_DURATION &&
      this.isParentUrlInListOfUrls(parentUrl, pushNotification.urls)
    ) {
      Bus.$emit('triggerPushNotification', pushNotification)
      return true
    }

    // Handle the TIME_ON_WEBSITE push notification
    this.pushNotifications.forEach(notification => {
      if (notification.triggerType !== config.TRIGGER_TYPES.TIME_ON_WEBSITE) {
        return
      }

      const messageReceivedTime = getItem(`messageReceivedTime${this.assistantId}`)
      if (
        notification.timeBeforeTrigger <= this.websiteTimeTracking.timeSpent &&
        isEventAcceptableInTime(messageReceivedTime, this.pushNotificationWaitTime)
      ) {
        Bus.$emit('triggerPushNotification', notification)
      }
    })

    // Clears the notification interval if no more TIME_ON_WEBSITE type notification available
    this.checkAndClearTimeTrackingNotificationInterval()
    return false
  }

  /**
   * Check and clear push notification interval when TIME_ON_WEBSITE type notification does not exists anymore
   */
  checkAndClearTimeTrackingNotificationInterval() {
    const isTimeOnWebsiteTypeNotificationExists = this.pushNotifications.some(
      notification => notification.triggerType === config.TRIGGER_TYPES.TIME_ON_WEBSITE
    )

    if (!isTimeOnWebsiteTypeNotificationExists) {
      clearInterval(this.timeTrackingNotificationInterval)
      this.timeTrackingNotificationInterval = null
    }
  }

  /**
   * Tests whether the url string parameter is found at least once in the urls array
   * @param {String} url
   * @param {Array<String>} urls
   * @returns {Boolean} true if the url string parameter is found at least once in the urls array
   */
  isUrlInList(url, urls) {
    /**
     * Example the url is `https://www.yelda.fr/blog`, we need to accept `https://www.yelda.fr/blog/latest`
     */
    return urls.some(urlElement => url.indexOf(urlElement) === 0)
  }

  /**
   * Tests whether the parent url is found at least once in the urls array
   * @param {String} parentUrl retrieved from location url parameter
   * @param {Array<String>} urls Array of url which should be compared to the parentUrl parameter or document parent url
   * @returns {Boolean} true if the parent url found at least once in the urls array
   */
  isParentUrlInListOfUrls(parentUrl, urls = []) {
    if (!urls.length) {
      return true
    }

    // Parent url retrieved from location query param
    // Since document.referrer only retrieved the parent url alone (not the path)
    if (parentUrl) {
      return this.isUrlInList(parentUrl, urls)
    }

    const parentFrameUrl = window.location === window.parent.location ? document.location.href : document.referrer
    return this.isUrlInList(parentFrameUrl, urls)
  }
}

export default new PushNotification()
